import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Collapse, CssBaseline, ListSubheader } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './PracticeSidebar.css';
import { infectionControlIcon, medicationIcon, oshaHipaaIcon } from '../icons';
import constants from '../constants';
import { Link } from 'react-router-dom';
import { HFSubMenu } from '../themes/components/ListItemButton';
import { useAppSelector } from '../redux/store';
import { UserAccessLevels, selectCurrentUser } from '../redux/user';
import { getEPayLink } from '../services/nodusService';
import { addError } from '../redux/alerts';
import { useAppDispatch } from '../redux/store';
import { LightTooltip } from './ToolTip.styled';
import { selectBalance } from '../redux/customer';
import useCustomerNumber from '../hooks/useCustomerNumber';
import useCustomer from '../hooks/useCustomer';

export default function PracticeSidebar() {
  const isDemoEnvironment = constants.ENV.toLowerCase() === 'demo';
  const dispatch = useAppDispatch();
  const [drawerName, setOpen] = React.useState('');
  const drawerWidth = 280;
  const currentUser = useAppSelector(selectCurrentUser);
  const customerNumber = useCustomerNumber()
  const customer = useCustomer()
  const metadata = customer.customerMetadata  

  const handleDropdown = (name: string | undefined) => {
    if (name === drawerName) {
      setOpen('');
    } else {
      setOpen(name || '');
    }
  };

  const hasPermission = (permissions: UserAccessLevels[]): boolean => permissions.some((perm) => perm === currentUser?.access_level);
  const openLinkToEPayPayments = async (): Promise<void> => {
    try {
      const link = await getEPayLink(customerNumber, 'payments');
      window.open(link, '_blank');
    } catch {
      dispatch(addError('Failed to open link'));
    }
  };
  const openLinkToEPayPaymentHistory = async (): Promise<void> => {
    try {
      const link = await getEPayLink(customerNumber, 'paymentsHistory');
      window.open(link, '_blank');
    } catch {
      dispatch(addError('Failed to open link'));
    }
  };
  const balance = useAppSelector(selectBalance);
  const showAutoclaveConfirmation = (metadata?.autoclave_outstanding ?? 0) > 0

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' }
          }}
          open
        >
          <List subheader={<ListSubheader>Account</ListSubheader>}>
            <ListItem key="Home" disablePadding>
              <ListItemButton component={Link} to="/practice/dashboard">
                <FontAwesomeIcon icon="house-chimney" size="sm" className="sidebar-icon" />
                <ListItemText primary="Home" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            {hasPermission([UserAccessLevels.Admin, UserAccessLevels.Owner]) && showAutoclaveConfirmation && (
              <ListItem key="Confirm Autoclave Type" disablePadding>
                <ListItemButton component={Link} to="/practice/biological/confirm-autoclave-type">
                  <FontAwesomeIcon icon="square-check" size="sm" className="sidebar-icon" />
                  <ListItemText primary="Confirm Autoclave Type" disableTypography className="sidebar-text" />
                </ListItemButton>
              </ListItem>
            )}
            {hasPermission([UserAccessLevels.Admin, UserAccessLevels.Owner]) && (
              <>
                <ListItem key="Pay Your Balance" disablePadding>
                  <ListItemButton onClick={() => openLinkToEPayPayments()}>
                    <FontAwesomeIcon icon={['far', 'credit-card']} size="sm" className="sidebar-icon" />
                    <ListItemText primary="Pay Your Balance" disableTypography className="sidebar-text" />
                    <LightTooltip placement="top" title={balance ? `$${balance}` : '$0.00'}>
                      <FontAwesomeIcon icon="circle-info" className="gray-icon" />
                    </LightTooltip>
                  </ListItemButton>
                </ListItem>
                <ListItem key="Payment History" disablePadding>
                  <ListItemButton onClick={() => openLinkToEPayPaymentHistory()}>
                    <FontAwesomeIcon icon={['far', 'file-lines']} size="sm" className="sidebar-icon" />
                    <ListItemText primary="Payment History" disableTypography className="sidebar-text" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
            <ListItem key="Shipment History" disablePadding>
              <ListItemButton component={Link} to="/practice/shipments/upcoming">
                <FontAwesomeIcon icon="truck" size="sm" className="sidebar-icon" />
                <ListItemText primary="Shipment Status" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Auto Renewals" disablePadding>
              <ListItemButton component={Link} to='/practice/auto-renewals'>
                <FontAwesomeIcon icon="arrows-rotate" size="sm" className="sidebar-icon" />
                <ListItemText primary="Auto Renewals" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            {hasPermission([UserAccessLevels.Admin, UserAccessLevels.Owner]) && (
              <ListItem key="Team Members" disablePadding>
                <ListItemButton component={Link} to='/practice/team-members'>
                  <FontAwesomeIcon icon="user" size="sm" className="sidebar-icon" />
                  <ListItemText primary="Team Members" disableTypography className="sidebar-text" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
          <Divider />
          <List subheader={<ListSubheader>Compliance Reports</ListSubheader>}>
            <ListItem key="Meds & Devices" disablePadding>
              <ListItemButton onClick={() => handleDropdown('meds')}>
                <div className="alt-icon">
                  <img src={medicationIcon} />
                </div>
                <ListItemText primary="Meds & Devices" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'meds' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'meds'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/practice/medications">
                  Medications
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/devices">
                  Devices
                </HFSubMenu>
              </List>
            </Collapse>
            <ListItem key="Infection Control" disablePadding>
              <ListItemButton onClick={() => handleDropdown('infection')}>
                <div className="alt-icon">
                  <img src={infectionControlIcon} />
                </div>
                <ListItemText primary="Infection Control" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'infection' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'infection'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/practice/biological">
                  Biological Monitoring
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/waterline-management">
                  Waterline Management
                </HFSubMenu>
              </List>
            </Collapse>
            <ListItem key="OSHA/HIPAA" disablePadding>
              <ListItemButton component={Link} to="/practice/osha-hipaa">
                <div className="alt-icon">
                  <img src={oshaHipaaIcon} alt="" />
                </div>
                <ListItemText primary="OSHA/HIPAA" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Waste Management" disablePadding>
              <ListItemButton onClick={() => handleDropdown('waste')}>
                <FontAwesomeIcon icon="earth-americas" size="lg" className="sidebar-icon" />
                <ListItemText primary="Waste Management" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'waste' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'waste'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/practice/medical-waste">
                  Sharps & Medical Waste
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/pharmaceutical-waste">
                  Pharmaceutical Waste
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/amalgam-waste">
                  Amalgam Waste
                </HFSubMenu>
              </List>
            </Collapse>
            <ListItem key="Tasks" disablePadding>
              <ListItemButton component={Link} to="/practice/tasks">
                <FontAwesomeIcon icon="bars-progress" size="sm" className="sidebar-icon" />
                <ListItemText primary="Tasks" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/practice/training">
                <FontAwesomeIcon icon="graduation-cap" size="sm" className="sidebar-icon" />
                <ListItemText primary="Training" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem key="Store" disablePadding>
              <ListItemButton component="a" target="_blank" href={constants.HEALTHFIRST_WEB_URL}>
                <FontAwesomeIcon icon="cart-shopping" size="sm" className="sidebar-icon" />
                <ListItemText primary="Store" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List subheader={<ListSubheader>Documents</ListSubheader>}>
            <ListItem key="SDS" disablePadding>
              <ListItemButton component={Link} to='/practice/sds'>
                <FontAwesomeIcon icon={['far', 'folder-open']} size="sm" className="sidebar-icon" />
                <ListItemText primary="SDS" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Reports" disablePadding>
              <ListItemButton component={Link} to='/practice/reports'>
                <FontAwesomeIcon icon="chart-column" className="sidebar-icon" />
                <ListItemText primary="Reports" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem key="Help Center" disablePadding>
              <ListItemButton onClick={() => handleDropdown('help')}>
                <FontAwesomeIcon icon="circle-question" className="sidebar-icon" />
                <ListItemText primary="Help Center" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'help' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'help'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/practice/contact">
                  Contact Us
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/faq">
                  FAQ
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/terms">
                  Terms & Conditions
                </HFSubMenu>
                <HFSubMenu component={Link} to="/practice/privacy-policy">
                  Privacy Policy
                </HFSubMenu>
                <HFSubMenu>Take the Tour</HFSubMenu>
                <HFSubMenu component={Link} to="/practice/guides">
                  Video Guides
                </HFSubMenu>
              </List>
            </Collapse>
            {isDemoEnvironment && (
              <ListItem key="Demo" disablePadding>
                <ListItemButton component={Link} to="/corp/demo">
                  <FontAwesomeIcon icon="star" className="sidebar-icon" />
                  <ListItemText primary="Demo" disableTypography className="sidebar-text" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Drawer>
      </Box>
    </Box>
  );
}
